import { useEffect, useState, useRef } from 'react';
import { CloudSDK } from '@sitecore-cloudsdk/core/browser';
import '@sitecore-cloudsdk/events/browser';
import useOneTrust from './useOneTrust';
import '@sitecore-cloudsdk/personalize/browser';
import { LayoutServicePageState, RenderingType } from '@sitecore-jss/sitecore-jss-nextjs';
import useCookieManager from './useCookieManager';

// Global state for CloudSDK initialization
let globalIsLoaded = false;
const listeners: Set<(isLoaded: boolean) => void> = new Set();

const notifyListeners = (isLoaded: boolean) => {
  globalIsLoaded = isLoaded;
  listeners.forEach((listener) => listener(isLoaded));
};

const useCloudSDK = (
  config?: { [key: string]: string },
  siteName?: string,
  pageState?: LayoutServicePageState,
  renderingType?: RenderingType
): { isCloudSDKLoaded: boolean; isPersonalizeEnabled: boolean } => {
  const [isCloudSDKLoaded, setIsCloudSDKLoaded] = useState(globalIsLoaded);
  const { consentGroups } = useOneTrust();
  const { removeCookie } = useCookieManager({
    domain:
      typeof window !== 'undefined' ? window.location.hostname.replace(/^www\./, '') : undefined,
  });
  const previousMarketingConsent = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    listeners.add(setIsCloudSDKLoaded);
    return () => {
      listeners.delete(setIsCloudSDKLoaded);
    };
  }, []);

  useEffect(() => {
    const currentMarketingConsent = consentGroups['marketing'];

    if (previousMarketingConsent.current && !currentMarketingConsent) {
      if (typeof document !== 'undefined') {
        const cookies = document.cookie.split(';');
        cookies.forEach((cookie) => {
          const cookieName = cookie.split('=')[0].trim();
          if (cookieName.startsWith('sc') || cookieName === 'bx_bucket_number') {
            removeCookie(cookieName);
          }
        });
      }
      globalIsLoaded = false;
      notifyListeners(false);
    }
    previousMarketingConsent.current = currentMarketingConsent;
  }, [consentGroups, config?.sitecoreEdgeContextId, removeCookie]);

  useEffect(() => {
    if (!config) return;

    if (process.env.NODE_ENV === 'development') {
      console.debug('Browser Events SDK is not initialized in development environment');
    } else if (
      pageState !== LayoutServicePageState.Normal ||
      renderingType === RenderingType.Component
    ) {
      console.debug('Browser Events SDK is not initialized in edit and preview modes');
    } else if (!globalIsLoaded && consentGroups['performance']) {
      CloudSDK({
        sitecoreEdgeUrl: config.sitecoreEdgeUrl,
        sitecoreEdgeContextId: config.sitecoreEdgeContextId,
        siteName: siteName || config.sitecoreSiteName,
        enableBrowserCookie: consentGroups['marketing'],
        // Replace with the top level cookie domain of the website that is being integrated e.g ".example.com" and not "www.example.com"
        cookieDomain: window.location.hostname.replace(/^www\./, ''),
      })
        .addEvents()
        .addPersonalize({
          enablePersonalizeCookie: consentGroups['marketing'],
          webPersonalization: consentGroups['marketing'],
        })
        .initialize();

      notifyListeners(true);
    }
  }, [config, siteName, pageState, consentGroups, renderingType]);

  return { isCloudSDKLoaded, isPersonalizeEnabled: consentGroups['marketing'] && isCloudSDKLoaded };
};

export default useCloudSDK;
